import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { useAuth } from "./AuthProvider";

interface LoadingScreenProps {
    message?: string;
}

const LoadingScreen: React.FC<LoadingScreenProps> = ({ message = "Loading..." }) => (
    <div
        style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100svh", // Small viewport height - accounts for mobile browser UI (fallback to 100vh in older browsers)
        }}
    >
        <div>{message}</div>
    </div>
);

interface RouteGuardConfig {
    /** If true, requires user to be authenticated */
    requireAuth?: boolean;
    /** If true, requires user to NOT be authenticated (public route) */
    requireUnauth?: boolean;
    /** If true, requires user to be a new user */
    requireNewUser?: boolean;
    /** If true, redirects users who need onboarding to onboarding (unless already on onboarding page) */
    redirectToOnboardingIfNeeded?: boolean;
    /** Redirect path when auth requirement not met */
    redirectTo?: string | ((user: any) => string);
    /** Custom message to show while redirecting */
    redirectMessage?: string;
}

interface RouteGuardProps {
    children: React.ReactNode;
    config: RouteGuardConfig;
}

/**
 * Base route guard component that handles authentication and authorization checks
 * Reduces code duplication across ProtectedRoute, PublicRoute, and OnboardingRoute
 */
export const RouteGuard: React.FC<RouteGuardProps> = ({ children, config }) => {
    const { isAuthenticated, user, isLoading, needsOnboarding, isProfileLoading } = useAuth();
    const router = useRouter();
    const [mounted, setMounted] = useState(false);

    const {
        requireAuth = false,
        requireUnauth = false,
        requireNewUser = false,
        redirectToOnboardingIfNeeded = false,
        redirectTo,
        redirectMessage = "Redirecting...",
    } = config;

    // Track client-side mount to prevent hydration mismatches
    useEffect(() => {
        setMounted(true);
    }, []);

    // Determine if user should have access
    const shouldAllowAccess = () => {
        // Still checking if loading or router not ready
        if (isLoading || !router.isReady) return null;

        // Check authentication requirements
        if (requireAuth && !isAuthenticated) return false;
        if (requireUnauth && isAuthenticated) return false;

        // Check new user requirement
        if (requireNewUser) {
            const isNewUser = user?.is_new_user || false;
            if (!isNewUser) return false;
        }

        return true;
    };

    // Handle redirects - only redirect when auth state is stable (not loading)
    useEffect(() => {
        if (!router.isReady || isLoading) return;

        // Get the current path
        const currentPath = router.asPath?.split("?")[0]?.split("#")[0] || router.pathname || "";

        // Handle redirect to onboarding if user needs onboarding (unless already on onboarding page)
        // Only redirect if user is authenticated (safety check)
        if (redirectToOnboardingIfNeeded && needsOnboarding && isAuthenticated) {
            const isOnOnboardingPage = currentPath === "/onboarding" || currentPath.startsWith("/onboarding/");

            if (!isOnOnboardingPage) {
                router.replace("/onboarding/personal-details");
                return;
            }
        }

        // Check if access should be denied
        let shouldDenyAccess = false;

        if (requireAuth && !isAuthenticated) shouldDenyAccess = true;
        if (requireUnauth && isAuthenticated) shouldDenyAccess = true;
        if (requireNewUser) {
            const isNewUser = user?.is_new_user || false;
            if (!isNewUser) shouldDenyAccess = true;
        }

        if (shouldDenyAccess && redirectTo) {
            const path = typeof redirectTo === "function" ? redirectTo(user) : redirectTo;
            router.replace(path);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isAuthenticated,
        isLoading,
        router.isReady,
        router.pathname,
        router.asPath,
        user?.is_new_user,
        needsOnboarding,
        requireAuth,
        requireUnauth,
        requireNewUser,
        redirectToOnboardingIfNeeded,
        redirectTo,
    ]);

    // During SSR and initial hydration, render children to prevent hydration mismatch
    // We'll handle redirects and access control after mount
    if (!mounted) {
        return <>{children}</>;
    }

    // Show loading only during initial auth check (when router is not ready)
    // For public routes, don't block during action loading (like login)
    if (!router.isReady) {
        return <LoadingScreen />;
    }

    // For public routes (requireUnauth), allow rendering even during loading if not authenticated
    // This prevents blocking the landing page during login action
    if (isLoading && requireUnauth && !isAuthenticated) {
        return <>{children}</>;
    }

    // For other routes, show loading during auth operations
    if (isLoading || (isAuthenticated && isProfileLoading)) {
        return <LoadingScreen />;
    }

    const allowAccess = shouldAllowAccess();

    // Show redirecting message
    if (allowAccess === false) {
        return <LoadingScreen message={redirectMessage} />;
    }

    // Render children if access is allowed
    if (allowAccess === true) {
        return <>{children}</>;
    }

    return null;
};
